import {Button, Badge} from "antd";
import styled from "styled-components";
import {useMediaQuery} from "@uidotdev/usehooks";
import {colors} from "../../../util/colors.ts";
import {PiLockKeyFill} from "react-icons/pi";
import {useSubscriptionEndDate} from "../../../hooks/useSubscriptionEndDate.ts";

type AiJob = {
    id: string;
    title: string;
    location: string;
    link: string;
    description: string; // Truncated for display
    fullDescription?: string; // Full description for cover letter
    matchScore: number;
}

interface JobPoolItemAiProps {
    item: AiJob;
    onJobClick?: (jobId: string) => void;
    requiresSubscription?: boolean;
}

export const JobPoolItemAi = ({item, onJobClick, requiresSubscription = false}: JobPoolItemAiProps) => {
    const isMobile = useMediaQuery("only screen and (max-width : 768px)");
    const {endDate} = useSubscriptionEndDate();
    const hasActiveSubscription = endDate && !isNaN(endDate.getTime()) && endDate > new Date();

    const handleAnsehenClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (requiresSubscription && !hasActiveSubscription) {
            // Open silverplus page in new tab for non-subscribed users
            window.open('/silverplus', '_blank');
        } else if (requiresSubscription && onJobClick) {
            onJobClick(item.id);
        } else {
            const url = item.link.startsWith('http') || item.link.startsWith('https')
                ? item.link
                : `https://${item.link.replace(/^\/+/, '')}`;
            window.open(url, '_blank');
        }
    };

    const onCoverLetterClick = async (job:AiJob)=>{
        // Check subscription status
        if (!hasActiveSubscription) {
            // Open silverplus page in new tab
            window.open('/silverplus', '_blank');
            return;
        }

        try {
            // For AI jobs, we don't have a job_demand_id since they're external search results
            // We pass the job details as text in jobRole parameter
            const { startXanoAiCoverLetterSession } = await import('../../../services/api');

            const title = job.title || '';
            // Use fullDescription if available, otherwise fall back to truncated description
            const description = job.fullDescription || job.description || '';
            const location = job.location || '';

            // Log warning if essential data is missing
            if (!title || !description) {
                console.warn('Cover letter: Missing job data', {
                    hasTitle: !!title,
                    hasDescription: !!description,
                    jobId: job.id
                });
            }

            const jobRole = [title, description, location ? `Location: ${location}` : ''].filter(Boolean).join('\n\n') || 'Job Position';

            // Pass null for job_demand_id since AI jobs are not in our database
            const sessionResponse = await startXanoAiCoverLetterSession(jobRole, null);

            if (!sessionResponse?.session_id) {
                console.error('Invalid session response:', sessionResponse);
                throw new Error('Failed to Bewerbungsschreiben erstellen session');
            }

            // Open cover letter page in new tab
            window.open(`/cover-letter/${sessionResponse.session_id}`, '_blank');
        } catch (error) {
            console.error('Error starting cover letter session:', error);
            // TODO: Show user-friendly error message
        }
    }

    return (
        <JobRowItemWrapper>
            <BgWrapper>
                <ItemDetails>
                    <InfoContainer>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                            <h3 style={{width: isMobile ? "fit-content" : "80%", marginTop: 0, color: 'black'}}>
                                {item.title}
                            </h3>
                            {requiresSubscription && (
                                <Badge
                                    count={
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            background: '#faad14',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: 500
                                        }}>
                                            <PiLockKeyFill size={12} />
                                            Silver+
                                        </div>
                                    }
                                />
                            )}
                        </div>
                        {item?.location &&
                            <PoolItemMeta>Ort: {item.location}</PoolItemMeta>}
                        {item?.description &&
                            <PoolItemMeta>Stellenbeschreibung: {item.description}</PoolItemMeta>}
                    </InfoContainer>
                </ItemDetails>
                <ItemBtns>
                    <Button
                        shape="round"
                        style={{width: isMobile ? '100%' : 200}}
                        type="primary"
                        onClick={handleAnsehenClick}
                    >
                        zur Stellenanzeige
                    </Button>
                    <Button
                        onClick={()=>onCoverLetterClick(item)}
                        style={{width: isMobile ? '100%' : 'auto'}}
                    >
                        Anschreiben erstellen
                    </Button>
                </ItemBtns>
            </BgWrapper>
        </JobRowItemWrapper>
    )
}

export const JobRowItemWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 767px) {
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
        padding: 0;
        border-bottom: none;
    }
`

export const RowItemWrapper = styled.div`
    padding: 0 100px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 900px) {
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
        padding: 0;
        border-bottom: none;
    }
`
export const BgWrapper = styled.div`
    width: 100%;
    padding: 8px;
    min-height: 130px;
    background: ${colors.darkBg} !important;
    border-bottom: 1px solid #d9d9d9;
    border-radius: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 767px) {
        background: ${colors.bg} !important;
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
        padding: 0;
        border-bottom: none;
    }
`

export const ItemDetails = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    width: 80%;
    justify-content: space-between;
    @media (max-width: 767px) {
        align-items: flex-start;
        flex-direction: column;
        width: calc(100% - 4px);
        padding: 8px;
        box-shadow: 0 .125rem .25rem rgba(45, 45, 45, .12), 0 .0625rem .1875rem rgba(45, 45, 45, .16), 0 0 .125rem rgba(45, 45, 45, .2);
        background: white;
        border-radius: 4px;
        margin: 0 2px;
    }
`

export const InfoContainer = styled.div`
    flex: 1;
    min-width: 0;
    @media (max-width: 767px) {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
    }
`

export const ItemBtns = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 16px;
    gap: 8px;
    flex-shrink: 0;
    @media (max-width: 767px) {
        padding-top: 3px;
        margin: 0;
        width: 100%;
        align-items: center;
    }
`

export const PoolItemMeta = styled.p`
    margin: 0 0 4px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    color: #696969
`;

